import { useState } from 'react';
import { bookingService } from '../../../../services/bookingService';
import { getBookingErrorDetails, getErrorMessage } from '../../../../utils/errorHandler';
import { prepareBookingRequest } from '../utils/checkoutHelpers';

export const usePayment = (
  bookingData,
  durationInfo,
  totals,
  isValidBooking,
  estimatedPrice,
  showNotification
) => {
  // PayU is the only payment method — pre-selected since there's nothing to choose.
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // PayU's classic Hosted Checkout (test.payu.in/_payment) is a form-POST
  // target, not a link — it needs key/txnid/amount/hash etc. submitted as
  // hidden fields. A plain GET redirect arrives with no params, which is why
  // PayU shows "Invalid amount". Build a real form and submit it instead.
  const submitPayUForm = (actionUrl, fields) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = actionUrl;
    form.style.display = 'none';

    Object.entries(fields).forEach(([key, value]) => {
      if (key === 'action_url' || value === null || value === undefined) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  // Hand off to PayU. Backend creates the booking + PayU payment in one call.
  // If it returns `payuData` (the classic Hosted Checkout flow — key, txnid,
  // amount, hash, ...) we auto-submit a hidden form to PayU. If it only
  // returns a bare `paymentUrl` (e.g. a REST-API-issued hosted-page link with
  // no extra fields needed) we just redirect. PayU redirects the browser back
  // to our backend's success/failure/cancel endpoints once payment is done,
  // which in turn redirect to /payment-success, /payment-failure or /payment-cancel.
  const redirectToPayU = (orderData) => {
    const paymentUrl = orderData?.paymentUrl || orderData?.payuPaymentUrl || orderData?.redirectUrl;
    const payuData = orderData?.payuData || orderData?.payUData || orderData?.payuParams;
    const hasFormFields = payuData && typeof payuData === 'object' && Object.keys(payuData).length > 0;

    if (!paymentUrl && !hasFormFields) {
      console.error('❌ No PayU payment URL/data returned by backend:', orderData);
      showNotification('Could not start PayU payment. Please try again or contact support.', 'error');
      setIsLoading(false);
      return;
    }

    showNotification('Redirecting to PayU secure payment page...', 'success');

    setTimeout(() => {
      if (hasFormFields) {
        console.log('💳 Submitting PayU hosted-checkout form:', payuData);
        submitPayUForm(payuData.action_url || paymentUrl, payuData);
      } else {
        console.log('💳 Redirecting to PayU:', paymentUrl);
        window.location.href = paymentUrl;
      }
    }, 500);
  };

  // Handle form submission
  const onSubmit = async (e, appliedCoupon) => {
    e.preventDefault();

    if (!termsAccepted) {
      showNotification('Please accept terms and conditions', 'error');
      return;
    }

    if (!isValidBooking) {
      showNotification('Invalid booking data', 'error');
      return;
    }

    if (!estimatedPrice || totals.total <= 0) {
      showNotification('Please wait for price calculation', 'error');
      return;
    }

    if (!totals.subtotal || !totals.total) {
      showNotification('Price calculation incomplete. Please refresh.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      console.log('📝 Creating booking...');
      const bookingRequest = prepareBookingRequest(
        bookingData,
        totals,
        durationInfo,
        paymentMethod,
        appliedCoupon
      );

      if (!bookingRequest.charges || !bookingRequest.finalAmount) {
        throw new Error('Invalid booking request: missing required amounts');
      }

      console.log('📤 Sending booking request:', bookingRequest);
      const bookingResponse = await bookingService.createBooking(bookingRequest);
      console.log('✅ Booking created:', bookingResponse);

      console.log('💳 Initiating PayU payment...');
      redirectToPayU(bookingResponse);
    } catch (error) {
      console.error('❌ Booking submission failed:', error);

      const errorInfo = getBookingErrorDetails(error);

      if (errorInfo.suggestions && errorInfo.suggestions.length > 0) {
        showNotification(
          errorInfo.message,
          'error',
          {
            suggestions: errorInfo.suggestions
          }
        );
      } else {
        showNotification(
          getErrorMessage(error) || 'Booking failed. Please try again.',
          'error'
        );
      }
      setIsLoading(false);
    }
  };

  return {
    paymentMethod,
    setPaymentMethod,
    termsAccepted,
    setTermsAccepted,
    isLoading,
    onSubmit
  };
};
